import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants";

const CreateInterview = () => {
  const { jobId, applicantId } = useParams();
  const [applicant, setApplicant] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("token");
  
  const [interviewData, setInterviewData] = useState({
    interview_date: "",
    notes: "",
    is_upcoming: true,
    is_active: true
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch applicant details
        const applicantResponse = await axios.get(`${jobsEndpoint}applicant_details/${applicantId}`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });
        setApplicant(applicantResponse.data);
        
        // Fetch job details
        const jobResponse = await axios.get(`${jobsEndpoint}${jobId}/`, {
          headers: {
            'Authorization': `Token ${token}`, 
            'Content-Type': 'application/json',
          }
        });
        setJob(jobResponse.data);
        
      } catch (err) {
        setError("Failed to load applicant or job details.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (jobId && applicantId) {
      fetchData();
    }
  }, [jobId, applicantId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInterviewData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!interviewData.interview_date) {
      setError("Please select an interview date and time.");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        interviewee: applicantId,
        interview_date: interviewData.interview_date,
        notes: interviewData.notes,
        is_upcoming: interviewData.is_upcoming,
        is_active: interviewData.is_active
      };

      await axios.post(`${jobsEndpoint}${jobId}/interview_create/`, payload, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      // Navigate back to applicants list
      navigate(`/applicants/${jobId}`);
      
    } catch (err) {
      setError("Failed to create interview. Please try again.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/applicants/${jobId}`);
  };

  // Get current date for min datetime input
  const getCurrentDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  if (loading) {
    return (
      <div className="lg:p-8">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error && !applicant) {
    return (
      <div className="lg:p-8">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <span 
            onClick={() => navigate("/jobs")}
            className="hover:text-gray-700 cursor-pointer"
          >
            Jobs
          </span>
          <span>›</span>
          <span 
            onClick={() => navigate(`/applicants/${jobId}`)}
            className="hover:text-gray-700 cursor-pointer"
          >
            Applicants
          </span>
          <span>›</span>
          <span className="text-gray-900">Schedule Interview</span>
        </div>
        <h1 className="text-2xl font-semibold text-gray-900">Schedule Interview</h1>
        <p className="mt-1 text-sm text-gray-600">
          Schedule an interview with {applicant?.user?.first_name} {applicant?.user?.last_name}
        </p>
      </div>

      {/* Applicant & Job Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Interview Details</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Job Position</h4>
            <p className="text-sm text-gray-600">{job?.title}</p>
            <p className="text-xs text-gray-500">{job?.company}</p>
          </div>
          
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Candidate</h4>
            <p className="text-sm text-gray-600">
              {applicant?.user?.first_name} {applicant?.user?.last_name}
            </p>
            <p className="text-xs text-gray-500">{applicant?.user?.email}</p>
          </div>
        </div>
      </div>

      {/* Interview Form */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Interview Information</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="interview_date" className="block text-sm font-medium text-gray-700 mb-2">
              Interview Date & Time *
            </label>
            <input
              type="datetime-local"
              id="interview_date"
              name="interview_date"
              value={interviewData.interview_date}
              onChange={handleInputChange}
              min={getCurrentDateTime()}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent text-gray-900"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Select the date and time for the interview</p>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Interview Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={interviewData.notes}
              onChange={handleInputChange}
              rows={4}
              placeholder="Enter any notes about the interview (e.g., interview type, location, topics to cover, etc.)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent resize-none text-gray-900 placeholder-gray-400"
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_upcoming"
                name="is_upcoming"
                checked={interviewData.is_upcoming}
                onChange={handleInputChange}
                className="h-4 w-4 text-[#19bfb7] focus:ring-[#19bfb7] border-gray-300 rounded"
              />
              <label htmlFor="is_upcoming" className="ml-2 block text-sm text-gray-700">
                Mark as upcoming interview
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={interviewData.is_active}
                onChange={handleInputChange}
                className="h-4 w-4 text-[#19bfb7] focus:ring-[#19bfb7] border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                Active interview
              </label>
            </div>
          </div>

          <div className="flex items-center gap-4 pt-4">
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2 text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Scheduling Interview..." : "Schedule Interview"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateInterview;